import moment from 'moment'
import calculatorEventBus from 'apps/bankrate-calculators/events'

/*
// This set of functions calculate amortization schedule for calculators.
// Use it as a mixin to make the methods accessible inside your vue
// For the functions to work, please be mindful of the following dependencies:
// - uses the following properties : loanAmount, interestRate, termInMonths
// - works only with vuex, needs a store.
// - the store should have the following:
//      - getters for pageHasErrors and dataChanged
//      - actions trackClick, updatePreviousUserInputs, setCurrencyValue, setDateValue, setCurrencyValue
//      - the store state should have : loanAmount, interestRate, termInMonths, startDate, estimatedPayoffDate,
//                                      monthlyExtraPayment, yearlyExtraPayment, yearlyExtraPaymentMonth
//                                      oneTimeExtraPayment, oneTimeExtraPaymentDate
// This mixin's incorporating vue needs a ref extraPaymentModule
 */
export default {
  methods: {
    getMonthlyPayment() {
      const rate = this.interestRate / 1200
      let factor = 1
      for (let i = 0; i < this.termInMonths; i++) {
        factor *= rate + 1
      }
      let loanAmount = this.loanAmount ? this.loanAmount : this.mortgageAmount

      return (loanAmount * factor * rate) / (factor - 1)
    },
    buildAmortizationSchedule() {
      const vm = this
      if (this.$store.getters.pageHasErrors) {
        return false
      }

      this.$nextTick(() => {
        const store = vm.$store.state
        const termInMonths = store.termInMonths
        const rate = store.interestRate / 1200
        const monthlyPayment = vm.getMonthlyPayment()
        let schedule = []
        let payoffDate = store.estimatedPayoffDate
        let totalPrincipalPaid = 0
        let totalInterestPaid = 0
        let balance = store.loanAmount
          ? store.loanAmount
          : vm.loanAmount
          ? vm.loanAmount
          : vm.mortgageAmount

        for (let i = 0; i < termInMonths; i++) {
          const rowDate = moment(vm.startDate).add(i + 1, 'M') // start at the next month

          // monthly payment
          let rowMonthlyPayment = monthlyPayment + store.monthlyExtraPayment
          rowMonthlyPayment +=
            rowDate.month() === store.yearlyExtraPaymentMonth
              ? store.yearlyExtraPayment
              : 0
          rowMonthlyPayment += rowDate.isSame(
            store.oneTimeExtraPaymentDate,
            'month'
          )
            ? store.oneTimeExtraPayment
            : 0

          // principal paid, interest and balance
          const rowInterestPaid = balance * rate
          let rowPrincipalPaid = rowMonthlyPayment - rowInterestPaid
          totalInterestPaid += rowInterestPaid
          totalPrincipalPaid += rowPrincipalPaid
          balance -= rowPrincipalPaid

          // adjust payment if balance becomes negative
          // Balance often becomes negative when there are extra payments.
          // Also, round balance to cent to avoid this from failing when balance positive but very tiny, for example 0.000000000024783
          if (i === termInMonths - 1 || Math.round(balance * 100) / 100 <= 0) {
            rowMonthlyPayment += balance
            rowPrincipalPaid = rowMonthlyPayment - rowInterestPaid
            totalPrincipalPaid += balance
            payoffDate = rowDate
            balance = 0

            // payoff date may have been changed, make sure one time extra payment is still valid
            if (store.oneTimeExtraPaymentDate.isAfter(payoffDate)) {
              vm.$refs.extraPaymentModule.errors.push(
                '* Single payment will fall after the new pay-off date.'
              )
              vm.$store.dispatch('setPageErrors', {
                name: 'oneTimeExtraPaymentDate',
                error: true
              })
            }
          }

          schedule[i] = {
            paymentDate: rowDate.format('MMM YYYY'),
            mobilePaymentDate: rowDate.format('MM/DD/YY'),
            payment: rowMonthlyPayment.toFixed(2),
            principalPaid: rowPrincipalPaid.toFixed(2),
            interest: rowInterestPaid.toFixed(2),
            totalInterest: totalInterestPaid.toFixed(2),
            balance: balance.toFixed(2)
          }
          if (balance <= 0) {
            break
          }
        }

        if (!store.pageHasErrors) {
          vm.$store.dispatch('updatePreviousUserInputs')
          vm.$store.dispatch('setCurrencyValue', {
            name: 'monthlyPayment',
            value: monthlyPayment
          })
          vm.$store.dispatch('setDateValue', {
            name: 'estimatedPayoffDate',
            value: payoffDate
          })
          vm.$store.dispatch('setCurrencyValue', {
            name: 'totalPrincipalPaid',
            value: totalPrincipalPaid
          })
          vm.$store.dispatch('setCurrencyValue', {
            name: 'totalInterest',
            value: totalInterestPaid
          })
          vm.amortizationSchedule = schedule
        }

        calculatorEventBus.$emit('amortizationDataCalculationComplete')
      })
    },
    updateAmortizationSchedule() {
      if (!this.$store.pageHasErrors && this.$store.getters.dataChanged) {
        this.buildAmortizationSchedule()
      }
    },
    printAmortizationSchedule(event) {
      this.$store.dispatch('trackClick', { event })
      let table = document.querySelector('[data-table=amortization]')
      let printWindow = window.open('')
      printWindow.document.write(table.outerHTML)
      printWindow.print()
      printWindow.close()
    }
  }
}
